<template>
  <div
    ref="searchBox"
    class="sidebar-search-box"
    :class="{
      'sidebar-search-box--notes': theme === 'notes',
      'sidebar-search-box--mocks': theme === 'mocks',
      'sidebar-search-box--open': categoriesExpanded,
    }"
  >
    <div class="sidebar-search-input-row">
      <input
        ref="searchInput"
        :value="value"
        type="search"
        placeholder="Search..."
        @input="$emit('input', $event.target.value)"
        @focus="expandCategories"
        @click.stop="expandCategories"
        @keyup.enter="submitSearch"
      />
      <button type="button" class="sidebar-search-go" aria-label="Search" @click.stop="submitSearch">
        <slot name="icon">
          <svg width="20" height="20" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M2.15 5.65c0 2.71 2.16 4.89 4.82 4.89s4.82-2.19 4.82-4.89S9.63.76 6.97.76 2.15 2.94 2.15 5.65zm1.47 0c0-1.88 1.48-3.4 3.35-3.4s3.35 1.54 3.35 3.41-1.51 3.4-3.35 3.4-3.35-1.54-3.35-3.41z"
              :fill="accentColor"
            />
            <path
              d="M.85 12.86a1.05 1.05 0 01-.05-1.23l2.89-3.18a1.05 1.05 0 111.56 1.4L2.06 12.81a1.05 1.05 0 01-1.21.05z"
              :fill="accentColor"
            />
          </svg>
        </slot>
      </button>
    </div>

    <transition name="sidebar-search-cats">
      <div v-if="categoriesExpanded" class="sidebar-search-checks">
        <label
          v-for="cat in localCategories"
          :key="cat.key"
          class="sidebar-search-check"
          :class="`sidebar-search-check--${cat.key}`"
        >
          <input type="checkbox" v-model="cat.checked" @change="emitCategories" />
          <span class="sidebar-search-check-box" aria-hidden="true"></span>
          <!-- <img v-if="cat.icon" :src="cat.icon" alt="" class="sidebar-search-check-icon" /> -->
          <span>{{ cat.label }}</span>
        </label>
      </div>
    </transition>
  </div>
</template>

<script>
const DEFAULT_CATEGORIES = () => ([
  { key: 'questions', label: 'Questions', checked: false, icon: '/images/questionmark.png' },
  { key: 'notes', label: 'Notes', checked: false, icon: '/images/file.png' },
  { key: 'journal', label: 'Journal', checked: false, icon: '/images/journal-icon.png' },
  { key: 'comment', label: 'Comment', checked: false, icon: '/images/comments-icon.svg' },
  { key: 'mocks', label: 'Mocks', checked: false, icon: '/images/brain.png' },
]);

export default {
  name: 'SidebarSearchBox',
  props: {
    value: { type: String, default: '' },
    theme: { type: String, default: 'questions' }, // questions | notes | mocks
  },
  data() {
    return {
      categoriesExpanded: false,
      localCategories: DEFAULT_CATEGORIES(),
    };
  },
  computed: {
    accentColor() {
      if (this.theme === 'notes') return '#faad1b';
      if (this.theme === 'mocks') return '#ED1C24';
      return '#115FFF';
    },
    activeCategories() {
      return this.localCategories.filter((c) => c.checked).map((c) => c.key);
    },
  },
  mounted() {
    document.addEventListener('mousedown', this.handleOutsideClick);
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.handleOutsideClick);
  },
  methods: {
    expandCategories() {
      this.categoriesExpanded = true;
    },
    collapseCategories() {
      this.categoriesExpanded = false;
      if (this.$refs.searchInput) {
        this.$refs.searchInput.blur();
      }
    },
    handleOutsideClick(event) {
      if (!this.categoriesExpanded) return;
      const box = this.$refs.searchBox;
      if (box && !box.contains(event.target)) {
        this.collapseCategories();
      }
    },
    emitCategories() {
      this.$emit('categories-change', this.activeCategories.slice());
    },
    submitSearch() {
      const q = String(this.value || '').trim();
      if (!q) {
        this.categoriesExpanded = true;
        return;
      }
      if (!this.activeCategories.length) {
        this.categoriesExpanded = true;
        return;
      }
      this.$emit('search', {
        q,
        categories: this.activeCategories.slice(),
      });
    },
  },
};
</script>

<style scoped>
.sidebar-search-box {
  border: 1px solid transparent;
  border-radius: 20px;
  box-sizing: border-box;
  max-height: 42px;
  padding: 0;
  background: #fff;
  margin-top: 25px;
  overflow: hidden;
  transition:
    max-height 0.32s ease,
    padding 0.28s ease,
    border-color 0.25s ease,
    box-shadow 0.25s ease;
}

.sidebar-search-box--open {
  max-height: 280px;
  padding: 10px;
  border-color: #1a90ff;
}

.sidebar-search-box--open.sidebar-search-box--notes {
  border-color: #faad1b;
}

.sidebar-search-box--open.sidebar-search-box--mocks {
  border-color: #ed1c24;
}

.sidebar-search-input-row {
  display: flex;
  align-items: center;
  position: relative;
  width: 100%;
}

.sidebar-search-input-row input {
  flex: 1;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  border: 0.5px solid #1a90ff;
  border-radius: 20px;
  padding: 6px 38px 6px 10px;
  font-size: 14px;
  font-family: Helveticacondensed, Helvetica, sans-serif;
  background: #fff;
  transition: width 0.28s ease, border-color 0.25s ease;
}

.sidebar-search-box--notes .sidebar-search-input-row input {
  border-color: #faad1b;
}

.sidebar-search-box--mocks .sidebar-search-input-row input {
  border-color: #ed1c24;
}

.sidebar-search-input-row input:focus {
  outline: none;
}

.sidebar-search-go {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  position: absolute;
  right: 10px;
}

.sidebar-search-checks {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-left: 6px;
  overflow: hidden;
}

.sidebar-search-cats-enter-active,
.sidebar-search-cats-leave-active {
  transition: opacity 0.22s ease, max-height 0.28s ease, margin 0.22s ease;
  max-height: 260px;
}

.sidebar-search-cats-enter,
.sidebar-search-cats-leave-to {
  opacity: 0;
  max-height: 0;
  margin-top: 0;
}

.sidebar-search-check {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  font-family: Helveticacondensed, Helvetica, sans-serif;
  color: #58595b;
  position: relative;
}

.sidebar-search-check input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.sidebar-search-check-box {
  width: 16px;
  height: 16px;
  border: 1.5px solid #939598;
  border-radius: 50%;
  flex-shrink: 0;
  background: #fff;
  position: relative;
  box-sizing: border-box;
}

.sidebar-search-check-box::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #c7c8ca;
  transform: translate(-50%, -50%);
  opacity: 0.55;
}

.sidebar-search-check input:checked + .sidebar-search-check-box {
  background: #fff;
  border-color: #939598;
}

.sidebar-search-check input:checked + .sidebar-search-check-box::after {
  opacity: 1;
  background: #1a90ff;
}

.sidebar-search-box--notes .sidebar-search-check input:checked + .sidebar-search-check-box::after {
  background: #fbad18;
}

.sidebar-search-box--mocks .sidebar-search-check input:checked + .sidebar-search-check-box::after {
  background: #ed1c24;
}

.sidebar-search-check-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
  flex-shrink: 0;
}
</style>
